/**
 * Platform Media Requirements
 *
 * Centralized configuration for what each social media platform
 * supports in terms of media types and content.
 */

import { PlatformType } from '@/services/channels.service';
import type { MediaItem } from '@/types/posts';

export type MediaType = 'IMAGE' | 'VIDEO' | 'PDF';

export interface PlatformMediaRequirements {
  /** Platform display name */
  name: string;
  /** Whether the platform supports text-only posts (no media) */
  supportsTextOnly: boolean;
  /** Supported media types for this platform */
  supportedMediaTypes: MediaType[];
  /** Whether the platform supports carousel/multi-image posts */
  supportsCarousel: boolean;
  /** Minimum media count (0 = text-only allowed, 1 = media required) */
  minMediaCount: number;
  /** Maximum media count for carousel */
  maxCarouselCount: number;
  /**
   * Whether the platform can publish a mix of IMAGE and VIDEO in a single post.
   * Only Instagram supports true mixed-media carousels.
   * All other platforms require homogeneous media (all images or all videos).
   */
  supportsMixedMedia: boolean;
}

/**
 * Platform media requirements configuration
 *
 * Rules:
 * - LinkedIn: Full support (text-only, images, videos, PDFs, carousels)
 * - Facebook: Text-only, images, videos, carousels (no PDFs)
 * - Instagram: REQUIRES media (no text-only), images, videos, carousels (no PDFs)
 * - Twitter/X: Text-only, images, videos, carousels (no PDFs)
 * - TikTok: Images, videos, carousels (no text-only, no PDFs)
 * - YouTube: REQUIRES video only (no images, no text-only, no PDFs)
 * - Pinterest: REQUIRES media (no text-only), images, videos, carousels (no PDFs)
 * - Threads: Text-only, images, videos, carousels (no PDFs)
 * - Bluesky: Text-only, images, videos, carousels (no PDFs)
 * - Google Business: Text, images; video is posted as its thumbnail image
 *   (GBP posts don't support video — the backend falls back to the video's
 *   thumbnail + caption when no other image is present)
 */
export const PLATFORM_MEDIA_REQUIREMENTS: Record<PlatformType, PlatformMediaRequirements> = {
  LINKEDIN: {
    name: 'LinkedIn',
    supportsTextOnly: true,
    supportedMediaTypes: ['IMAGE', 'VIDEO', 'PDF'],
    supportsCarousel: true,
    minMediaCount: 0,
    maxCarouselCount: 20,
    supportsMixedMedia: false,
  },
  FACEBOOK: {
    name: 'Facebook',
    supportsTextOnly: true,
    supportedMediaTypes: ['IMAGE', 'VIDEO'],
    supportsCarousel: true,
    minMediaCount: 0,
    maxCarouselCount: 10,
    supportsMixedMedia: false,
  },
  INSTAGRAM: {
    name: 'Instagram',
    supportsTextOnly: false,
    supportedMediaTypes: ['IMAGE', 'VIDEO'],
    supportsCarousel: true,
    minMediaCount: 1,
    maxCarouselCount: 10,
    supportsMixedMedia: true,
  },
  TWITTER: {
    name: 'X (Twitter)',
    supportsTextOnly: true,
    supportedMediaTypes: ['IMAGE', 'VIDEO'],
    supportsCarousel: true,
    minMediaCount: 0,
    maxCarouselCount: 4,
    supportsMixedMedia: false,
  },
  TIKTOK: {
    name: 'TikTok',
    supportsTextOnly: false,
    supportedMediaTypes: ['IMAGE', 'VIDEO'],
    supportsCarousel: true,
    minMediaCount: 1,
    maxCarouselCount: 35,
    supportsMixedMedia: false,
  },
  YOUTUBE: {
    name: 'YouTube',
    supportsTextOnly: false,
    supportedMediaTypes: ['VIDEO'],
    supportsCarousel: false,
    minMediaCount: 1,
    maxCarouselCount: 1,
    supportsMixedMedia: false,
  },
  PINTEREST: {
    name: 'Pinterest',
    supportsTextOnly: false,
    supportedMediaTypes: ['IMAGE', 'VIDEO'],
    supportsCarousel: true,
    minMediaCount: 1,
    maxCarouselCount: 5,
    supportsMixedMedia: false,
  },
  THREADS: {
    name: 'Threads',
    supportsTextOnly: true,
    supportedMediaTypes: ['IMAGE', 'VIDEO'],
    supportsCarousel: true,
    minMediaCount: 0,
    maxCarouselCount: 10,
    supportsMixedMedia: false,
  },
  GOOGLE_MY_BUSINESS: {
    name: 'Google Business',
    supportsTextOnly: true,
    // Video is "supported" at the composer level because the backend falls
    // back to posting the video's thumbnail image + caption (GBP posts don't
    // accept actual video). See getGmbVideoThumbnailNotice() for the
    // non-blocking UI notice shown to the user.
    supportedMediaTypes: ['IMAGE', 'VIDEO'],
    supportsCarousel: true,
    minMediaCount: 0,
    maxCarouselCount: 10,
    supportsMixedMedia: false,
  },
  BLUESKY: {
    name: 'Bluesky',
    supportsTextOnly: true,
    supportedMediaTypes: ['IMAGE', 'VIDEO'],
    supportsCarousel: true,
    minMediaCount: 0,
    maxCarouselCount: 4,
    supportsMixedMedia: false,
  },
  CANVA: {
    name: 'Canva',
    supportsTextOnly: false,
    supportedMediaTypes: [],
    supportsCarousel: false,
    minMediaCount: 0,
    maxCarouselCount: 0,
    supportsMixedMedia: false,
  },
  GDRIVE: {
    name: 'Google Drive',
    supportsTextOnly: false,
    supportedMediaTypes: [],
    supportsCarousel: false,
    minMediaCount: 0,
    maxCarouselCount: 0,
    supportsMixedMedia: false,
  },
  WORDPRESS: {
    name: 'WordPress',
    supportsTextOnly: false,
    supportedMediaTypes: [],
    supportsCarousel: false,
    minMediaCount: 0,
    maxCarouselCount: 0,
    supportsMixedMedia: false,
  },
};

/**
 * Get platform requirements with fallback for unknown platforms
 */
export const getPlatformMediaRequirements = (platform: PlatformType): PlatformMediaRequirements => {
  return PLATFORM_MEDIA_REQUIREMENTS[platform] || {
    name: platform,
    supportsTextOnly: true,
    supportedMediaTypes: ['IMAGE', 'VIDEO'],
    supportsCarousel: true,
    minMediaCount: 0,
    maxCarouselCount: 10,
    supportsMixedMedia: false,
  };
};

/**
 * Returns true when the media array contains at least one IMAGE and at least
 * one VIDEO — i.e. it is a mixed image+video post.
 *
 * This is the condition that restricts publishing to Instagram-only, because
 * Instagram is the only platform that can publish a mixed-media carousel.
 */
export const hasMixedImageVideo = (media: MediaItem[]): boolean => {
  const hasImage = media.some((m) => m.type === 'image');
  const hasVideo = media.some((m) => m.type === 'video');
  return hasImage && hasVideo;
};

/**
 * Check whether a platform can publish a mix of images and videos in one post.
 *
 * @param platform - The platform to check
 * @param media - Full MediaItem array for the effective post (including inherited variation media)
 * @returns Object with isCompatible flag and reason if not compatible
 *
 * Returns isCompatible=true when:
 *  - The media is not mixed (all images or all videos) — no restriction applies
 *  - The platform supports mixed media (currently only Instagram)
 *
 * Returns isCompatible=false only when the media IS mixed AND the platform
 * does NOT support mixed-media posts.
 */
export const checkMixedMediaCompatibility = (
  platform: PlatformType,
  media: MediaItem[]
): { isCompatible: boolean; reason?: string } => {
  if (!hasMixedImageVideo(media)) {
    return { isCompatible: true };
  }
  const requirements = getPlatformMediaRequirements(platform);
  if (requirements.supportsMixedMedia) {
    return { isCompatible: true };
  }
  return {
    isCompatible: false,
    reason: `${requirements.name} can't post images and video together — only Instagram can. Post them separately.`,
  };
};

/**
 * Check if a platform can accept the given media configuration
 *
 * @param platform - The platform to check
 * @param mediaTypes - Array of media types in the post (empty = text-only)
 * @returns Object with isCompatible flag and reason if not compatible
 */
export const checkPlatformMediaCompatibility = (
  platform: PlatformType,
  mediaTypes: MediaType[]
): { isCompatible: boolean; reason?: string } => {
  const requirements = getPlatformMediaRequirements(platform);

  // Check if text-only post is allowed
  if (mediaTypes.length === 0) {
    if (!requirements.supportsTextOnly) {
      return {
        isCompatible: false,
        reason: `${requirements.name} requires media`,
      };
    }
    return { isCompatible: true };
  }

  // Check if media types are supported
  for (const mediaType of mediaTypes) {
    if (!requirements.supportedMediaTypes.includes(mediaType)) {
      if (mediaType === 'PDF') {
        return {
          isCompatible: false,
          reason: `${requirements.name} doesn't support PDFs`,
        };
      }
      if (mediaType === 'IMAGE' && requirements.supportedMediaTypes.length === 1 && requirements.supportedMediaTypes[0] === 'VIDEO') {
        return {
          isCompatible: false,
          reason: `${requirements.name} only supports video`,
        };
      }
      return {
        isCompatible: false,
        reason: `${requirements.name} doesn't support ${mediaType.toLowerCase()}s`,
      };
    }
  }

  // Check carousel limits
  if (mediaTypes.length > 1) {
    if (!requirements.supportsCarousel) {
      return {
        isCompatible: false,
        reason: `${requirements.name} doesn't support multiple media`,
      };
    }
    if (mediaTypes.length > requirements.maxCarouselCount) {
      return {
        isCompatible: false,
        reason: `${requirements.name} allows max ${requirements.maxCarouselCount} items`,
      };
    }
  }

  return { isCompatible: true };
};

/**
 * Returns true when the given MediaItem is an animated GIF.
 *
 * Detection priority:
 *  1. `item.mimeType === 'image/gif'`  (set by upload pipeline when backend reports GIF)
 *  2. filename suffix `.gif`
 *  3. url suffix `.gif` (covers external-URL imports and S3 signed URLs)
 */
export const isGifMedia = (item: MediaItem): boolean => {
  if (item.mimeType === 'image/gif') return true;
  if (item.filename?.toLowerCase().endsWith('.gif')) return true;
  // Strip query-string before checking URL extension
  try {
    const path = new URL(item.url).pathname;
    if (path.toLowerCase().endsWith('.gif')) return true;
  } catch {
    if (item.url?.toLowerCase().endsWith('.gif')) return true;
  }
  return false;
};

/**
 * Platforms where GIFs are auto-converted by the backend at publish time.
 *   - Instagram  → GIF is converted to MP4 video
 *   - Google Business → GIF is converted to a static JPG
 *
 * These channels must remain fully selectable — the notice is informational only.
 */
const GIF_AUTO_CONVERT_PLATFORMS = new Set<PlatformType>([
  'INSTAGRAM',
  'GOOGLE_MY_BUSINESS',
]);

/**
 * Returns a non-blocking informational notice when the given platform will
 * auto-convert a GIF at publish time, or `undefined` when no notice is needed.
 *
 * The channel stays enabled and selectable — this is purely an info hint.
 * Returns `undefined` when:
 *  - no GIF is present in `media`, OR
 *  - the platform doesn't perform GIF auto-conversion.
 */
export const getGifAutoConvertNotice = (
  platform: PlatformType,
  media: MediaItem[]
): string | undefined => {
  if (!GIF_AUTO_CONVERT_PLATFORMS.has(platform)) return undefined;
  if (!media.some(isGifMedia)) return undefined;

  const name = getPlatformMediaRequirements(platform).name;
  if (platform === 'INSTAGRAM') {
    return `This GIF will be auto-converted to video for ${name}.`;
  }
  // GOOGLE_MY_BUSINESS
  return `This GIF will be auto-converted to an image for ${name}.`;
};

/**
 * Returns a non-blocking informational notice when a video will be published
 * to Google Business Profile as its thumbnail image (GBP posts don't support
 * actual video), or `undefined` when no notice is needed.
 *
 * The channel stays enabled and selectable — this is purely an info hint.
 * Returns `undefined` when:
 *  - the platform isn't Google Business, OR
 *  - `media` has no VIDEO item, OR
 *  - `media` also has an IMAGE — the image is what actually gets used, so no
 *    thumbnail fallback happens and the notice would be misleading.
 */
export const getGmbVideoThumbnailNotice = (
  platform: PlatformType,
  media: MediaItem[]
): string | undefined => {
  if (platform !== 'GOOGLE_MY_BUSINESS') return undefined;

  const hasVideo = media.some((m) => m.type === 'video');
  if (!hasVideo) return undefined;

  const hasImage = media.some((m) => m.type === 'image');
  if (hasImage) return undefined;

  return `Google Business Profile doesn't support video, so your video's thumbnail image will be posted with the caption.`;
};

/**
 * Filter channels that are compatible with given media types
 */
export const filterCompatibleChannels = <T extends { platform: PlatformType }>(
  channels: T[],
  mediaTypes: MediaType[]
): { compatible: T[]; incompatible: Array<T & { incompatibilityReason: string }> } => {
  const compatible: T[] = [];
  const incompatible: Array<T & { incompatibilityReason: string }> = [];

  for (const channel of channels) {
    const { isCompatible, reason } = checkPlatformMediaCompatibility(channel.platform, mediaTypes);
    if (isCompatible) {
      compatible.push(channel);
    } else {
      incompatible.push({ ...channel, incompatibilityReason: reason || 'Incompatible media' });
    }
  }

  return { compatible, incompatible };
};
